<template>
  <div class="pet-game">
    <h1>宠物养成</h1>

    <div class="pet-image">
      <img :src="petImage || defaultImage" alt="宠物形象喵" />
    </div>

    <div class="pet-status">
      <p>饥饿值: {{ hunger }}</p>
      <p>快乐值: {{ happiness }}</p>
      <p>清洁值: {{ cleanliness }}</p>
      <p>等级: {{ level }}</p>
    </div>

    <transition name="fade">
      <div
        v-if="activeMinigame"
        class="minigame-container"
        :class="minigameType"
      >
        <div class="slider-track">
          <div
            class="slider-thumb"
            :style="{ left: thumbPosition + '%' }"
          ></div>
          <div class="target-area high-score" :style="highTargetStyle"></div>
          <div class="target-area low-score" :style="lowTargetStyle"></div>
        </div>
        <button class="confirm-button" @click="handleMiniGameResult">
          OK!
        </button>
      </div>
    </transition>

    <div class="pet-actions" v-show="!activeMinigame">
      <template v-for="(action, index) in actions" :key="index">
        <button
          @click="startMiniGame(action.type)"
          :disabled="isActionDisabled(action.type)"
          :class="action.type"
        >
          {{ action.label }}
        </button>
      </template>
    </div>

    <div class="pet-message">
      <p>{{ message }}</p>
    </div>
  </div>
</template>

<script>
const STATUS_CONFIG = {
  BASE_VALUE: 0,
  DECREMENT_INTERVAL: 5000,
  SAVE_INTERVAL: 10000,
  THRESHOLD: {
    CRITICAL: 20,
    LEVEL_UP: 50,
  },
  MAX_LEVEL: 100,
};

const ACTIONS = [
  {
    label: "喂食喵",
    type: "feed",
    color: "#FFC107",
    baseSpeed: 1.2,
    baseWidth: 30,
    speedFactor: 0.15,
  },
  {
    label: "玩耍喵",
    type: "play",
    color: "#4CAF50",
    baseSpeed: 1.0,
    baseWidth: 35,
    speedFactor: 0.12,
  },
  {
    label: "洗澡喵",
    type: "clean",
    color: "#2196F3",
    baseSpeed: 1.5,
    baseWidth: 25,
    speedFactor: 0.18,
  },
  {
    label: "睡觉喵",
    type: "sleep",
    color: "#9C27B0",
    baseSpeed: 1.0,
    baseWidth: 28,
    speedFactor: 0.1,
  },
  {
    label: "升级喵",
    type: "levelUp",
  },
];

import { petImages } from "@/utils/petImages";

export default {
  name: "PetGame",
  data() {
    return {
      hunger: STATUS_CONFIG.BASE_VALUE,
      happiness: STATUS_CONFIG.BASE_VALUE,
      cleanliness: STATUS_CONFIG.BASE_VALUE,
      level: 1,
      message: "欢迎领养你的虚拟宠物喵～",
      defaultImage: petImages.happy,
      intervals: [],

      activeMinigame: null,
      minigameType: "",
      thumbPosition: 0,
      thumbSpeed: 2,
      highTarget: { start: 30, width: 10 },
      lowTarget: { start: 50, width: 20 },
      isThumbMoving: false,
      moveDirection: 1,
      currentAction: null,
    };
  },

  computed: {
    petImage() {
      return this.hunger <= STATUS_CONFIG.THRESHOLD.CRITICAL
        ? petImages.hungry
        : this.happiness <= STATUS_CONFIG.THRESHOLD.CRITICAL
        ? petImages.sad
        : this.cleanliness <= STATUS_CONFIG.THRESHOLD.CRITICAL
        ? petImages.dirty
        : petImages.happy;
    },

    actions() {
      return ACTIONS.map((action) => ({
        ...action,
        disabled: action.type === "levelUp" ? !this.canLevelUp : false,
      }));
    },

    canLevelUp() {
      return [this.hunger, this.happiness, this.cleanliness].every(
        (v) => v >= STATUS_CONFIG.THRESHOLD.LEVEL_UP
      );
    },

    highTargetStyle() {
      return {
        left: this.highTarget.start + "%",
        width: this.highTarget.width + "%",
        backgroundColor: this.currentAction?.color + "40",
        borderColor: this.currentAction?.color,
      };
    },

    lowTargetStyle() {
      return {
        left: this.lowTarget.start + "%",
        width: this.lowTarget.width + "%",
        backgroundColor: this.currentAction?.color + "20",
        borderColor: this.currentAction?.color,
      };
    },
  },

  methods: {
    generateNewTarget() {
      const levelFactor = Math.sqrt(this.level / STATUS_CONFIG.MAX_LEVEL);
      this.thumbSpeed =
        this.currentAction.baseSpeed *
        (1 + this.currentAction.speedFactor * levelFactor);
      const highWidth = Math.max(
        this.currentAction.baseWidth * (1 - levelFactor * 0.9) * 0.4,
        4
      );
      const lowWidth = Math.max(
        this.currentAction.baseWidth * (1 - levelFactor * 0.9) * 0.8,
        8
      );
      const maxStart = 100 - (highWidth + lowWidth + 5);
      const baseStart = Math.max(0, Math.random() * maxStart);

      this.lowTarget = {
        start: Math.max(0, baseStart - 5),
        width: Math.min(lowWidth + 10, 100 - (baseStart - 5)),
      };

      this.highTarget = {
        start: baseStart,
        width: highWidth,
      };
    },

    handleMiniGameResult() {
      this.isThumbMoving = false;
      this.activeMinigame = false;

      const checkHit = (target) => {
        return (
          this.thumbPosition >= target.start &&
          this.thumbPosition <= target.start + target.width
        );
      };

      let delta = 0;
      if (checkHit(this.highTarget)) {
        delta = 30 + Math.floor(this.level * 0.2);
        this.message = `完美命中！获得${delta}点${this.getStatusLabel()}喵～`;
      } else if (checkHit(this.lowTarget)) {
        delta = 15 + Math.floor(this.level * 0.1);
        this.message = `不错哦！获得${delta}点${this.getStatusLabel()}喵～`;
      } else {
        delta = Math.floor(Math.random() * 5);
        this.message =
          delta > 0
            ? `勉强获得${delta}点${this.getStatusLabel()}喵～`
            : "哎呀，没对准喵～下次再试吧";
      }

      if (delta > 0) {
        this.updateStatus(this.minigameType, delta);
      }
    },

    levelUp() {
      if (this.level >= STATUS_CONFIG.MAX_LEVEL) {
        this.message = "宠物已经达到最高等级喵！";
        return;
      }

      if (this.canLevelUp) {
        this.level = Math.min(this.level + 1, STATUS_CONFIG.MAX_LEVEL);
        ["hunger", "happiness", "cleanliness"].forEach((prop) => {
          this[prop] = STATUS_CONFIG.BASE_VALUE;
        });
        this.message = `宠物升级了喵！等级: ${this.level}`;
      } else {
        this.message = "宠物太饿、太累或太脏了，无法升级喵～";
      }
    },

    startMiniGame(type) {
      if (type === "levelUp") {
        this.levelUp();
        return;
      }

      this.activeMinigame = true;
      this.minigameType = type;
      this.currentAction = ACTIONS.find((a) => a.type === type);
      this.generateNewTarget();
      this.startThumbMovement();
    },

    startThumbMovement() {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(
        navigator.userAgent
      );
      if (isMobile) {
        this.thumbSpeed *= 0.8;
      }
      this.isThumbMoving = true;
      this.moveDirection = 1;
      this.thumbPosition = 0;

      const move = () => {
        if (!this.isThumbMoving) return;

        this.thumbPosition = Math.max(
          0,
          Math.min(
            100,
            this.thumbPosition + this.thumbSpeed * this.moveDirection
          )
        );

        if (this.thumbPosition >= 100) {
          this.moveDirection = -1;
        } else if (this.thumbPosition <= 0) {
          this.moveDirection = 1;
        }

        requestAnimationFrame(move);
      };
      requestAnimationFrame(move);
    },

    getStatusLabel() {
      const map = {
        feed: "饱食度",
        play: "快乐值",
        clean: "清洁值",
        sleep: "精力值",
      };
      return map[this.minigameType];
    },

    updateStatus(type, delta) {
      const statusMap = {
        feed: "hunger",
        play: "happiness",
        clean: "cleanliness",
        sleep: "happiness",
      };
      const prop = statusMap[type];

      if (type === "sleep") {
        this[prop] = Math.min(Math.max(this[prop] + delta, 0), 100);
        this.hunger = Math.min(Math.max(this.hunger - 5, 0), 100);
      } else {
        this[prop] = Math.min(Math.max(this[prop] + delta, 0), 100);
      }
      if (delta < 5) {
        this.thumbSpeed *= 0.95;
        this.highTarget.width *= 1.05;
      }
    },

    isActionDisabled(type) {
      if (type === "levelUp") return !this.canLevelUp;
      return !!this.activeMinigame;
    },

    reduceStats() {
      this.addInterval(() => {
        ["hunger", "happiness", "cleanliness"].forEach((prop) => {
          this[prop] = Math.max(this[prop] - 1, 0);
        });

        if (
          [this.hunger, this.happiness, this.cleanliness].some((v) => v === 0)
        ) {
          this.message = "宠物需要你的照顾喵～";
        }
      }, STATUS_CONFIG.DECREMENT_INTERVAL);
    },

    addInterval(callback, interval) {
      this.intervals.push(setInterval(callback, interval));
    },

    clearIntervals() {
      this.intervals.forEach(clearInterval);
      this.intervals = [];
    },

    saveProgress() {
      const persistData = {
        hunger: this.hunger,
        happiness: this.happiness,
        cleanliness: this.cleanliness,
        level: this.level,
      };
      localStorage.setItem("petData", JSON.stringify(persistData));
    },

    loadProgress() {
      const savedData = localStorage.getItem("petData");
      if (savedData) {
        const data = JSON.parse(savedData);
        Object.assign(this, data);
      }
    },
  },

  mounted() {
    this.loadProgress();
    this.reduceStats();
    this.addInterval(this.saveProgress, STATUS_CONFIG.SAVE_INTERVAL);
  },

  beforeUnmount() {
    this.clearIntervals();
  },
};
</script>

<style scoped>
.pet-game {
  font-family: Arial, sans-serif;
  text-align: center;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  max-height: 100vh;
  overflow-y: auto;
}

.pet-image img {
  width: 100%;
  max-width: 150px;
  height: auto;
  margin-bottom: 20px;
}

.pet-status p {
  font-size: 16px;
  margin: 8px 0;
}

.pet-actions {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 10px;
  max-width: 450px;
  margin: 0 auto;
}

.pet-actions button {
  margin: 0;
  padding: 12px 0;
  width: 100%;
  font-size: 14px;
  cursor: pointer;
  background-color: #ffcc00;
  border: none;
  border-radius: 5px;
}

.pet-actions button:hover {
  background-color: #ffb700;
}

.pet-actions button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pet-actions button.feed {
  background: #ffc107;
}
.pet-actions button.play {
  background: #4caf50;
}
.pet-actions button.clean {
  background: #2196f3;
}
.pet-actions button.sleep {
  background: #9c27b0;
}
.pet-actions button.levelUp {
  background: #ff5722;
}

.minigame-container {
  position: relative;
  margin: 20px auto;
  padding: 20px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 85%;
  max-width: 450px;
}

.slider-track {
  position: relative;
  height: 25px;
  background: #f0f0f0;
  border-radius: 12px;
  margin: 15px 0;
  overflow: visible;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1;
}

.slider-thumb {
  position: absolute;
  width: 12px;
  height: 25px;
  left: 0;
  transform: translateX(-50%);
  border: 2px solid white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  transition: left 0.05s linear !important;
  will-change: left;
}

.target-area {
  position: absolute;
  height: 100%;
  border-radius: 8px;
  border: 2px solid;
  z-index: 1;
  transition: all 0.3s ease;
}

.confirm-button {
  display: block;
  margin: 15px auto 0;
  padding: 10px 25px;
  font-size: 16px;
  font-weight: bold;
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
}

.minigame-container.feed {
  border: 2px solid #ffc107;
}
.minigame-container.feed .slider-thumb {
  background: #ffc107;
}
.minigame-container.feed .confirm-button {
  background: #ffc107;
}

.minigame-container.play {
  border: 2px solid #4caf50;
}
.minigame-container.play .slider-thumb {
  background: #4caf50;
}
.minigame-container.play .confirm-button {
  background: #4caf50;
}

.minigame-container.clean {
  border: 2px solid #2196f3;
}
.minigame-container.clean .slider-thumb {
  background: #2196f3;
}
.minigame-container.clean .confirm-button {
  background: #2196f3;
}

.minigame-container.sleep {
  border: 2px solid #9c27b0;
}
.minigame-container.sleep .slider-thumb {
  background: #9c27b0;
}
.minigame-container.sleep .confirm-button {
  background: #9c27b0;
}

.confirm-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 5px 8px rgba(0, 0, 0, 0.2);
}

.target-area.high-score {
  z-index: 3;
  border-width: 2px;
  background-color: rgba(255, 215, 0, 0.4);
  top: 0%;
}

.target-area.low-score {
  z-index: 2;
  background-color: rgba(144, 238, 144, 0.3);
  border-style: dotted;
  top: 0%;
  max-width: 100%;
}

.pet-message p {
  font-size: 20px;
  color: #ff6600;
  margin-top: 5px;
}

@media screen and (max-width: 480px) {
  .pet-actions {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 8px;
  }

  .pet-actions button {
    padding: 10px 0;
    font-size: 12px;
  }
}
</style>
